// Home.jsx

import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { throttle } from 'lodash';


const Home = () => {

    const [isScrolled, setIsScrolled] = useState(false);
  const prevScrollY = useRef(0);
  const rafId = useRef<number | null>(null);

  useEffect(() => {
    const handleScroll = () => {
      if (rafId.current) return;
      
      rafId.current = window.requestAnimationFrame(() => {
        const currentScrollY = window.scrollY;
        const hasScrolled = currentScrollY > 0;
        
        if (hasScrolled !== (prevScrollY.current > 0)) {
          setIsScrolled(hasScrolled);
        }
        
        prevScrollY.current = currentScrollY;
        rafId.current = null;
      });
    };

    const throttledScroll = throttle(handleScroll, 100);
    window.addEventListener('scroll', throttledScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', throttledScroll);
      if (rafId.current) {
        window.cancelAnimationFrame(rafId.current);
      }
    };
  }, []);

    
  return (
    <div className="bg-gray-50 min-h-screen">
       {/* Navigation - Enhanced */}
       <nav className={`bg-navy-900 sticky top-0 ${
      isScrolled ? 'z-[1200]' : 'z-[50]'
    } shadow-xl backdrop-blur-sm border-b border-navy-700 transition-z duration-300`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex justify-between items-center">
        <div className="flex items-center space-x-2">
          <div className="w-3 h-3 bg-navy-700 rounded-full animate-pulse" />
          <h1 className="text-2xl font-bold text-white tracking-tighter">Business Solutions</h1>
        </div>
        <Link 
          to="/contact" 
          className="bg-navy-700/50 text-white px-6 py-2 rounded-full hover:bg-navy-800 transition-all border border-navy-600 hover:border-navy-400"
        >
          Contacto <span className="ml-2">→</span>
        </Link>
      </div>
    </nav>

      {/* Enhanced Hero Section */}
      <header className="relative bg-gradient-to-br py-16 from-navy-900 to-navy-800 min-h-[85vh] flex items-center overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Text Content */}
            <div className="text-white space-y-8">
              <div className="inline-block bg-navy-700/30 px-4 py-2 rounded-full mb-6 border border-navy-500">
                <span className="text-sm">Trusted by Fortune 500 Companies</span>
              </div>
              <h1 className="text-5xl md:text-6xl font-bold leading-tight">
                Smart Digital  
                <span className="bg-gradient-to-r from-indigo-600 to-blue-600 bg-clip-text text-transparent"> Transformation</span>
              </h1>
              <p className="text-xl md:text-2xl text-gray-200 max-w-2xl leading-relaxed">
  We drive business innovation through technological solutions,  
  <span className="border-b-2 border-navy-400">adaptive</span> and proven growth strategies.
</p>

              
                {/* Strategic Metrics */}
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
    {[
        { value: '15+', label: 'Years of Experience' },
        { value: '98%', label: 'Satisfaction' },
        { value: '500+', label: 'Clients' },
        { value: '24/7', label: 'Support' }
    ].map((metric, index) => (
        <div key={index} className="bg-white/5 p-4 rounded-xl border border-white/10">
        <div className="text-2xl font-bold text-navy-300">{metric.value}</div>
        <div className="text-sm text-gray-300 mt-1">{metric.label}</div>
        </div>
    ))}
    </div>


                        {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 mt-12">
            <Link
                to="/contact"
                className="bg-navy-500 text-white px-8 py-4 rounded-xl text-lg font-semibold hover:bg-navy-600 transition-all flex items-center justify-center gap-2"
            >
                <span>Start Free Consultation</span>
                <span className="text-xl">↗</span>
            </Link>
            <Link
                to="/cases"
                className="bg-transparent border-2 border-navy-400 text-white px-8 py-4 rounded-xl hover:bg-navy-400/10 transition-all"
            >
                View Case Studies
            </Link>
            </div>

            </div>

            {/* Interactive Graphic - Fixed Alignment */}
            <div className="relative hidden lg:block">
              <div className="absolute inset-0 bg-gradient-to-br from-navy-400/20 to-transparent rounded-2xl -rotate-3 transform origin-[85%_15%]" />
              <div className="relative bg-white/5 p-8 rounded-2xl border border-white/10 backdrop-blur-sm space-y-6">
                <div className="animate-float">
                  <div className="bg-navy-700/30 p-6 rounded-xl border border-navy-500">
                    <div className="flex items-center gap-4">
                      <div className="bg-navy-400 w-12 h-12 rounded-lg flex items-center justify-center">
                        <span className="text-2xl">📈</span>
                      </div>
                      <div>
                        <div className="text-sm text-gray-300">Average Growth</div>
                        <div className="text-2xl font-bold text-white">+200% ROI</div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bg-navy-700/30 p-6 rounded-xl border border-navy-500 mt-8 animate-float-delayed">
                  <div className="h-32 bg-gradient-to-r from-navy-400 to-navy-600 rounded-lg flex items-center justify-center">
                    <span className="text-4xl">🚀</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Animated Background Elements */}
        <div className="absolute inset-0 z-0">
          <div className="absolute top-0 right-0 w-1/3 h-full bg-gradient-to-l from-navy-900/50 to-transparent" />
          <div className="absolute bottom-0 left-0 w-32 h-32 bg-navy-400 rounded-full blur-3xl opacity-20 animate-pulse" />
        </div>
      </header>

  {/* Enhanced Packages Section */}
<section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
  <div className="text-center mb-16">
    <h2 className="text-4xl md:text-5xl font-bold text-navy-900 mb-4">
      Custom Packages
    </h2>
    <p className="text-gray-600 max-w-2xl mx-auto text-lg">
      Scalable solutions for every stage of business growth
    </p>
  </div>
  
  <div className="grid md:grid-cols-3 gap-8 lg:gap-12">
    {['Basic', 'Premium', 'Enterprise'].map((packageName, index) => (
      <div 
        key={packageName}
        className="group bg-white p-8 rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 border-2 border-transparent hover:border-navy-100 relative overflow-hidden"
      >
        <div className="absolute inset-0 bg-gradient-to-b from-white to-navy-50 opacity-0 group-hover:opacity-100 transition-opacity" />
        
        <div className="relative z-10">
          <div className="bg-gradient-to-r from-navy-700 to-navy-800 p-5 rounded-xl mb-6 text-center">
            <h3 className="text-2xl font-bold text-white">{packageName}</h3>
          </div>
          
          <p className="text-gray-600 mb-6 text-center">
            {index === 0 && 'Start of digital operations'}
            {index === 1 && 'Optimization of key processes'}
            {index === 2 && 'Complete business transformation'}
          </p>
          
          <div className="mb-8">
            <div className="text-4xl font-bold text-navy-900 text-center mb-4">
              {index === 0 && '$9,999'}
              {index === 1 && '$24,999'}
              {index === 2 && 'Custom'}
            </div>
            
            <ul className="space-y-4">
              {[
                'Comprehensive management',
                '24/7 support',
                'Scalability',
                'Monthly reports',
                index >= 1 ? 'Advanced analytics' : 'Basic analytics',
                index >= 2 ? 'Strategic consulting' : 'General advisory'
              ].map((item) => (
                <li key={item} className="flex items-center text-navy-800">
                  <svg 
                    className="w-5 h-5 text-navy-600 mr-3 flex-shrink-0" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path 
                      strokeLinecap="round" 
                      strokeLinejoin="round" 
                      strokeWidth="2" 
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  {item}
                </li>
              ))}
            </ul>
          </div>
          
          <button className="w-full bg-gradient-to-r from-navy-700 to-navy-600 text-white py-4 rounded-xl font-semibold hover:from-navy-800 hover:to-navy-700 transition-all transform hover:scale-[1.02]">
            More Information
          </button>
        </div>
      </div>
    ))}
  </div>
</section>

{/* Enhanced VIP Support Section */}
<section className="relative bg-navy-900 text-white py-24 overflow-hidden">
  <div className="absolute inset-0 opacity-5 bg-[url('https://www.transparenttextures.com/patterns/concrete-wall.png')]" />
  
  <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-2 gap-16 items-center">
    <div className="relative space-y-8">
      <h2 className="text-4xl md:text-5xl font-bold mb-6">
        <span className="bg-gradient-to-r from-navy-400 to-white bg-clip-text text-transparent">
          VIP Support
        </span>
        <br />for Businesses
      </h2>
      
      <p className="text-xl text-gray-300 leading-relaxed">
        Premium service with direct access to our executive team and priority solutions
      </p>
      
      <div className="space-y-6">
        {[
          { 
            title: 'Personalized Advisor', 
            icon: '👑',
            desc: 'Exclusive dedication from an expert in your industry' 
          },
          { 
            title: 'Response in less than 1h', 
            icon: '⏱',
            desc: 'Guaranteed 24/7 response times' 
          },
          { 
            title: 'Strategic Reports', 
            icon: '📊',
            desc: 'Predictive analysis and executive reports' 
          }
        ].map((benefit) => (
          <div 
            key={benefit.title}
            className="flex items-start bg-navy-800/30 p-6 rounded-2xl border border-navy-700 hover:border-navy-500 transition-all"
          >
            <div className="bg-navy-700 p-3 rounded-xl mr-4 text-2xl">
              {benefit.icon}
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-2">{benefit.title}</h3>
              <p className="text-gray-400">{benefit.desc}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
    
    <div className="relative">
      <div className="absolute -top-8 -right-8 w-32 h-32 bg-navy-400 rounded-full blur-3xl opacity-20" />
      <div className="relative bg-white rounded-2xl p-1 shadow-2xl overflow-hidden">
        <img 
          src="/support-team.jpg" 
          alt="VIP Support" 
          className="rounded-xl object-cover w-full h-[500px]"
        />
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-navy-900/90 to-transparent p-8">
          <div className="text-white">
            <h3 className="text-xl font-bold mb-2">Certified Team</h3>
            <p className="text-gray-300">Professionals with 10+ years of average experience</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</section>
{/* Sección de Casos Destacados */}
<section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
  <div className="text-center mb-16">
    <h2 className="text-4xl md:text-5xl font-bold text-navy-900 mb-4">
      Transformative Successes
    </h2>
    <p className="text-gray-600 max-w-2xl mx-auto text-lg">
      Real stories of companies reaching new levels of productivity
    </p>
  </div>
  
  <div className="grid md:grid-cols-3 gap-8">
    {[
      {
        industry: 'Manufacturing',
        result: '+230% ROI',
        metric: 'Reduction in operational costs',
        icon: '🏭'
      },
      {
        industry: 'Retail',
        result: '+150% Sales',
        metric: 'Growth in digital channels',
        icon: '🛍'
      },
      {
        industry: 'Technology',
        result: '40h → 8h',
        metric: 'Optimization of critical processes',
        icon: '💻'
      }
    ].map((caseStudy, index) => (
      <div 
        key={index}
        className="bg-white p-8 rounded-2xl shadow-xl hover:shadow-2xl transition-all group border-2 border-transparent hover:border-navy-100"
      >
        <div className="bg-navy-100 w-fit p-5 rounded-2xl mb-6 text-4xl">
          {caseStudy.icon}
        </div>
        <h3 className="text-2xl font-bold text-navy-900 mb-4">
          {caseStudy.industry}
        </h3>
        <div className="text-4xl font-bold text-navy-800 mb-4">
          {caseStudy.result}
        </div>
        <p className="text-gray-600 mb-6">
          {caseStudy.metric}
        </p>
        <div className="flex items-center text-navy-700">
          <span className="mr-2">📈 View full case study</span>
          <span className="transition-transform group-hover:translate-x-2">→</span>
        </div>
      </div>
    ))}
  </div>
</section>


    </div>
  );
};

export default Home;